<template>
  <SplNuxtLink
    v-if="isAvailable"
    :to="targetUrl"
    :query="query"
    :is-external="isExternal"
    property="offers"
    itemprop="offers"
    itemscope
    itemtype="https://schema.org/Offer"
    rel="nofollow"
  >
    <div
      class="purchase-link"
      :class="{
        'purchase-link--single-price': !isBasePriceDispayed,
        acent: variant === 'acent',
        secondary: variant === 'secondary',
      }"
    >
      <div class="purchase-link__content">
        <div
          v-if="isBasePriceDispayed"
          class="purchase-link__base-price"
        >
          {{ basePriceStr }}
        </div>
        <div class="purchase-link__best-price">
          {{ bestPriceStr }}
        </div>
        <div
          class="purchase-link__label"
          :class="{ 'purchase-link__label--single-price': !isBasePriceDispayed }"
        >
          <Lang
            v-if="isBuy"
            by-key="buy"
          />
          <Lang
            v-if="isBook"
            by-key="book"
          />
        </div>

        <meta
          property="highPrice"
          itemprop="highPrice"
          :content="basePriceValue"
        >
        <meta
          property="price"
          itemprop="price"
          :content="bestPriceValue"
        >
        <meta
          property="lowPrice"
          itemprop="lowPrice"
          :content="bestPriceValue"
        >
        <meta
          property="priceCurrency"
          itemprop="priceCurrency"
          content="EUR"
        >
      </div>
    </div>
  </SplNuxtLink>
</template>

<script lang="ts">
import SplNuxtLink from '../shared/SplNuxtLink.vue'

import { Service } from '~/core/ts/entity/Service'
import { Money } from '~/core/ts/entity/Money'

import { type ServiceBookingQuery } from '~/core/ts/entity/ServiceBookingQuery'

export default defineNuxtComponent({
  components: {
    SplNuxtLink,
  },
  props: {
    query: {
      type: Object as PropType<ServiceBookingQuery>,
      required: true,
    },
    isAvailable: {
      type: Boolean,
      required: true,
    },
    basePrice: {
      type: Object as PropType<Service['price']>,
      required: true,
    },
    bestPrice: {
      type: Object as PropType<Service['price']>,
      required: true,
    },
    isExternal: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String as PropType<ServiceBookingQuery['buyVariant']>,
      required: true,
    },
    targetUrl: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const isBuy = props.type === 'buy'
    const isBook = props.type === 'booking'

    const isBasePriceDispayed = computed(() => props.basePrice.amount !== props.bestPrice.amount)
    const basePriceStr = computed(() => Money.toString(props.basePrice))
    const bestPriceStr = computed(() => Money.toString(props.bestPrice))
    const variant = computed(() => (props.type === 'buy' ? 'secondary' : 'acent'))

    const basePriceValue = (props.basePrice.amount / 100).toFixed(2)
    const bestPriceValue = (props.bestPrice.amount / 100).toFixed(2)

    return {
      isBuy,
      isBook,

      isBasePriceDispayed,
      basePriceStr,
      bestPriceStr,
      variant,
      basePriceValue,
      bestPriceValue,
    }
  },
})
</script>

<style lang="scss" scoped>
.snippet {
  @apply h-0;
  @apply opacity-0;
  display: none;
}

.purchase-link {
  @apply font-bold;
  @apply py-6;

  &__content {
    @apply flex flex-row-reverse items-center justify-center;
    @apply gap-2;

    @screen lg {
      @apply flex flex-row;
    }
  }

  &__label {
    @apply font-normal;
    @apply normal-case;
    @apply row-span-2;
    @apply text-right;
    @apply text-lg;

    &--single-price {

    }
  }

  &__base-price {
    @apply text-lg;
    @apply line-through;
    @apply text-right;

    @apply opacity-60;
  }

  &__best-price {
    @apply text-4xl;
    @apply text-right;
  }
}

@screen lg {
  .purchase-link {
    @apply h-auto;
    @apply flex flex-row items-center justify-center;
    @apply gap-6;

    &__label {
      @apply text-xl uppercase font-bold;
      @apply border-l border-white;
      @apply pl-4;
    }

    &__base-price {
      @apply text-base;
      @apply line-through;
      @apply text-right;
      @apply font-normal;
    }

    &__best-price {
      @apply text-xl;
    }
  }
}

.purchase-link.acent {
  @apply bg-spl-acent-dark;
  @apply text-white;
}

.purchase-link.secondary {
  @apply bg-spl-secondary;
  @apply text-spl-dark;
}
</style>
